<template>
	<el-row>
		<el-col>
			<div class="base-form header">
				<h2>Автомобили контрагентов</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddVehicleClick">
					Новый автомобиль
				</el-button>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="6">
			<div class="base-form">
				<el-input
					v-model="tableFilterSearchQuery"					
					placeholder="Поиск автомобилей по VIN, гос. номеру, марке и модели, контрагенту"
					@keyup.enter="fetchVehiclesAsync"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>			
		</el-col>

		<el-col :span="6">
			<div class="base-form">
				<counterparty-filter-component @selected="onCounterpartyFilterChanged" />
			</div>			
		</el-col>		
	</el-row>

	<el-row>
		<el-col :span="24">
			<el-table
				:data="tableData"
				v-loading="tableLoading"
				sortable="custom"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
				@sort-change="onTableSortingChange"
				@row-dblclick="onTableRowDblClick">			
				<template #empty>
					<el-text type="info">Список автомобилей пуст ☹️</el-text>
				</template>	
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onTablePickVehicleClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Автомобиль" sortable prop="model">
					<template #default="scope">
						<el-link 
							:underline="false" 
							type="primary"
							@click="(e) => onTableVehicleClick(scope.row, e)">
							{{ scope.row.model.manufacturer.name }}
							{{ scope.row.model.name }}
						</el-link>
					</template>
				</el-table-column>
				<el-table-column label="Контрагент"  prop="model.manufacturer.name">
					<template #default="scope">
						{{ scope.row.counterparty.lastname }}
						{{ scope.row.counterparty.firstname }}
						<div v-if="scope.row.counterparty?.legalEntity?.name">
							<el-text type="info" size="small">
								{{ scope.row.counterparty.legalEntity.name }}
							</el-text>
						</div>
					</template>
				</el-table-column>
				<el-table-column prop="vin" label="VIN" />
				<el-table-column prop="govermentalNumber" label="Гос. номер" />
				<el-table-column prop="productionDate" label="Год выпуска" />
				<el-table-column prop="mileage" label="Пробег" />
				<el-table-column v-if="!isPickerMode" align="right" width="120">
					<template #default="scope">
						<el-button
							size="small"
							type="danger"
							@click="(e) => onTableDeleteVehicleClick(scope.row, e)">
							Удалить
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import store from '@/store';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElNotification, ElMessageBox } from 'element-plus';
import Vehicle from '@/models/Vehicle';
import TabsDictionary from '@/variables/tabs';
import axiosHelper from '@/helpers/axiosHelper';
import EventHelper from '@/helpers/eventsHelper';
import ApplicationTab from '@/models/ApplicationTab';
import OrderApiHelper from '@/helpers/api/OrderApiHelper';
import CounterpartyFilterComponent from '@/components/filters/CounterpartyFilterComponent.vue';
import CounterpartyModel from '@/models/CounterpartyModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import VehicleApiHelper from '@/helpers/api/VehicleApiHelper';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink,
		CounterpartyFilterComponent
	},
	props: {
		params: Array
	}
})
export default class VehiclesView extends Vue {
	/** props */
	params!: Array<any>;

	/** constants */
	isPickerMode = false;
	onPickedCallback: Function | null = null;	
	queryVehiclesHttpClient = axiosHelper.createInstance(store.getters.jwt);
	reloadTableEvent = 'vehiclesListUpdate';
	circleIcon = CirclePlus;
	searchIcon = Search;
	
	/** view-models */	
	tableLoading = false;
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'model';
	tableSortOrder = 'ascending';
	tableFilterCounterpatyId: number | null = null;
	tableFilterSearchQuery: string | null = null;

	tableData: Array<Vehicle> = [];
	
	mounted() {
		this.initView();
		
		EventHelper.setEventListener(this.reloadTableEvent, this.fetchVehiclesAsync);
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	unmounted() {
		EventHelper.removeEventListener(this.reloadTableEvent, this.fetchVehiclesAsync);
	}

	initView() {
		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		/** check if picker mode activated */
		if (pickerModeArgument?.isPickerModeEnabled === true &&
			pickerModeArgument?.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback!;
		}
	}

	onAddVehicleClick() {
		const appTab = TabsDictionary.vehicleAdd;
		store.commit('addTab', appTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableRowDblClick(vehicle: Vehicle) {
		const orderViewTab = TabsDictionary.vehicleView;
		orderViewTab.params = [ vehicle, true ];
		orderViewTab.allowMultiple = true;
		store.commit('addTab', orderViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableVehicleClick(vehicle: Vehicle, e: MouseEvent) {
		e.stopPropagation();

		const orderViewTab = TabsDictionary.vehicleView;
		orderViewTab.params = [ vehicle, true ];
		orderViewTab.allowMultiple = true;
		store.commit('addTab', orderViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableSortingChange(eventObject: any) {
		this.tableSortColumn = eventObject.prop;
		this.tableSortOrder = eventObject.order;
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	onCounterpartyFilterChanged(counterparty?: CounterpartyModel) {
		if (counterparty?.id) {
			this.tableFilterCounterpatyId = counterparty.id;
		} else {
			this.tableFilterCounterpatyId = null;
		}

		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	/** handles callback and passes selected vehicle to a parent component (if picker-mode is enabled) */	
	onTablePickVehicleClick(vehicle: Vehicle, e: Event) {
		e.stopPropagation();
		if (this.onPickedCallback) {
			this.onPickedCallback(vehicle);
		}
	}

	async fetchVehiclesAsync() {
		try {
			this.tableLoading = true;
			const filterObject = {
				// operations: this.tableOperationsFilter?.length ? this.tableOperationsFilter.join(',') : null,				
				filterCounterpartyId: this.tableFilterCounterpatyId,
				orderBy: this.tableSortColumn,
				orderAsc: this.tableSortOrder == 'ascending',
				query: this.tableFilterSearchQuery
			};
			const response = await this.queryVehiclesHttpClient.get('/api/vehicles', { params: filterObject });
			if (!response.data?.length) {
				this.tableData = [];
				return;
			}
			this.tableData = response.data.map((_: any) => new Vehicle(_));
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: 'Ошибка соединения, не удалось загрузить список автомобилей',
					type: 'error',
				});
			} else {
				ElNotification({
					message: 'Ошибка загрузки списка автомобилей',
					type: 'error',
				});
			}
			console.error('Error loading vehicles list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async deleteVehicleAsync(vehicle: Vehicle) {
		this.tableLoading = true;
		const vehicleApiHelper = new VehicleApiHelper();
		const isDeleted = await vehicleApiHelper.deleteVehicleAsync(store.getters.jwt, vehicle.id);
		if (isDeleted) {
			EventHelper.dispatchEvent(this.reloadTableEvent);
			ElNotification({				
				message: `Автомобиль ${vehicle.model?.manufacturer?.name} ${vehicle.model?.name} удален`,
				type: 'success',
			});
		} else {
			ElNotification({				
				message: `Ошибка удаления автомобиля ${vehicle.model?.manufacturer?.name} ${vehicle.model?.name}`,
				type: 'error',
			});
		}
		this.tableLoading = false;
	}

	onTableDeleteVehicleClick(vehicle: Vehicle, e: Event) {
		e.stopPropagation();
			
		new OrderApiHelper(store.getters.jwt)
			.getOrdersByVehicleAsync(vehicle.id)
			.then(orders => {
				if (orders!.length != 0) {
					ElMessageBox.confirm(
						'Удаление невозможно, у автомобиля есть заказ-наряды', {
						confirmButtonText: 'OK',
						type: 'error',
					});
				} else {
					ElMessageBox.confirm(
						`Автомобиль ${vehicle.model?.manufacturer?.name} ${vehicle.model?.name} будет удален`, {
						confirmButtonText: 'OK',
						cancelButtonText: 'Отмена',
						type: 'warning',
					})
					.then(() => this.deleteVehicleAsync(vehicle));
				}
			});
	}
}
</script>